import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { taskColumns } from '@utils/columns';

import withPagination from '@shared/withFilteredPagination';
import FilterTable from '@shared/FilterTable';
import TaskResourceList from './components/TaskResourceList';
import ArchiveTaskAction from './components/ArchiveTaskAction';

import { AppProvider, Frame, Toast, Button, Icon, ButtonGroup, Select } from '@shopify/polaris';
import { ListMajorMonotone, SoftPackMajorMonotone } from '@shopify/polaris-icons';

const propTypes = {
  records: PropTypes.array.isRequired,
  newUrl: PropTypes.string,
  logisticsTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showArchivedFilter: PropTypes.bool,
}

const defaultProps = {

}

const TaskIndex = (props) => {
  const [view, setView] = useState("table");
  const [toast, setToast] = useState(null);

  // Operations loses track of unassigned work in the day to day, so this opens the one list they
  // check: our own logistics team, nobody assigned. The team comes from a setting, not a fixed id.
  const unassignedURL = () => {
    const params = new URLSearchParams({ status: 'unassigned' });
    if (props.logisticsTeamId) params.set('team_id', props.logisticsTeamId);

    return `/tasks?${params.toString()}`;
  };

  const primaryAction = props.newUrl ? { content: I18n.t("actions.create_x", { x: I18n.t("activerecord.models.task.one") }), url: props.newUrl } : null;
  
  const hideToast = () => {
    setToast(null);
  }
 
  const setListView = () => setView("list");
  const setTableView = () => setView("table")

  // If view isn't list then don't bother processing the component
  const resourceListView = () => {
    if(view == "list")
      return ( 
      <TaskResourceList 
          loading={ props.loading } 
          items={ props.records }  
          itemProps={{
            extraActions: (taskId, task) => (
              <div className="flex mt4">
                  { task.archive_url &&
                    <div className="mr8">
                      <ArchiveTaskAction archiveURL={ task.archive_url } />
                    </div>
                  }
                  <div>
                    <Button
                        primary
                        url={`/tasks/${taskId}`}>
                      {I18n.t("actions.view_details")}
                    </Button>
                  </div>
              </div>
            )
          }}
        />
    )
  }

  const TaskListMemo = useCallback(resourceListView, [props.loading, props.records, view]);
  return(
    <AppProvider>
      <FilterTable
        searchName="search_query"
        searchPlaceholder={"ID de Orden"}
        pageProps={{
          title: I18n.t("activerecord.models.task.other"),
          primaryAction: primaryAction,
        }}
        columnsInfo={ taskColumns }
        resourceTableProps={{
          itemURLAsAction: false,
          mobileCellLabels: true,
        }}
        contentComponent={ view == "list" ? TaskListMemo : null }
        resourceTableClassName={ "resource-table-tasks" }
        filterFooter={
          <div className="mt20" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button url={ unassignedURL() }>Sin asignar de Logística</Button>

            <Select
              label="Traslados"
              labelInline
              disabled={ props.loading }
              options={[{ label: 'Todos', value: ' ' }, { label: 'A proveedor/taller', value: 'true' }]}
              onChange={ (value) => props.handleFilterChange(value, 'supplier_leg') }
              value={ props.filterState?.supplier_leg || ' ' }
            />

            {/* Archived tasks are out of the daily list; this is the only way to see them. */}
            { props.showArchivedFilter &&
            <Select
              label="Tareas"
              labelInline
              disabled={ props.loading }
              options={[{ label: 'Activas', value: ' ' }, { label: 'Archivadas', value: 'true' }]}
              onChange={ (value) => props.handleFilterChange(value, 'archived') }
              value={ props.filterState?.archived || ' ' }
            />
            }

            <ButtonGroup segmented>
              <Button onClick={ setTableView } primary={ view == "table" } monochrome><Icon source={ SoftPackMajorMonotone}/></Button>
              <Button onClick={ setListView } primary={ view == "list" }><Icon source={ListMajorMonotone} /></Button>
            </ButtonGroup>
          </div>
        }
        {...props}
      />

      { toast &&
        <div style={{position: 'absolute'}}>
          <Frame>
            <Toast content={ toast } onDismiss={hideToast} />
          </Frame>
        </div>
      }
  
    </AppProvider>
  )
};

TaskIndex.propTypes = propTypes;
TaskIndex.defaultProps = defaultProps;

// A blank filter is " ", not "", so a value coming from the URL has to replace the whole default.
// Reading them here is what makes a filtered view shareable: the link reopens the same list.
const filtersFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const defaults = { search_query: "", kind: " ", status: " ", team_id: " ", courier_id: " ", archived: " ", supplier_leg: " " };

  return Object.keys(defaults).reduce((filters, key) => {
    const value = params.get(key);

    return value ? { ...filters, [key]: value } : filters;
  }, defaults);
};

export default withPagination(TaskIndex, {
  mapPropsToInitialFilters: filtersFromURL,
  mapPropsToInitialRecords: (props) => props.records,
  mapPropsToPagy: (props) => props.pagy,
  paramsName: "task_filters",
  filterURL: "/tasks/filter",
});